import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffectiveUser } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FaFileContract, FaUserCheck, FaExclamationTriangle, FaBan, FaUsers, FaShieldAlt, FaGlobe } from 'react-icons/fa';
import Header from '../components/Header';
import ThemeProvider from '../components/ThemeProvider';
import { motion } from 'framer-motion';

const ForumAgreementPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useEffectiveUser();
  const [loading, setLoading] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [language, setLanguage] = useState('en'); // 'en' for English, 'zh' for Traditional Chinese

  // Language content
  const content = {
    en: {
      title: "Community Forum Agreement",
      subtitle: "Before accessing our community forum, please read and accept the following terms and guidelines.",
      premiumAccess: {
        title: "Premium Community Access",
        description: "Our community forum is an exclusive premium feature designed to create a high-quality environment for meaningful financial discussions. As a premium member, you're joining a select community of users committed to mutual respect and constructive dialogue.",
        highlight: "🌟 This is a premium feature - help us maintain the high standards that make this community valuable."
      },
      communityStandards: {
        title: "Community Standards",
        intro: "As a premium member, you agree to:",
        items: [
          "Maintain respectful and constructive discussions",
          "Focus conversations on personal finance and money management",
          "Protect the privacy of all community members",
          "Use appropriate language and professional tone",
          "Report inappropriate content to help maintain quality",
          "Contribute meaningfully to help others achieve their financial goals"
        ]
      },
      confidentiality: {
        title: "Confidentiality & Premium Access",
        intro: "This forum is an exclusive premium feature. You agree to:",
        items: [
          "Keep all forum discussions confidential and not share content outside the platform",
          "Not screenshot, copy, or redistribute forum conversations",
          "Respect that this is a private premium community",
          "Never share sensitive personal or financial information"
        ],
        highlight: "🔒 What happens in our premium forum, stays in our premium forum."
      },
      prohibited: {
        title: "What's Not Allowed",
        items: [
          "Harassment, bullying, or disrespectful behavior",
          "Spam, advertising, or off-topic discussions",
          "Sharing forum content outside the platform",
          "Providing false information or impersonating others",
          "Any illegal activities or harmful content"
        ]
      },
      enforcement: {
        title: "Enforcement",
        description1: "Violations of these guidelines will result in account suspension or termination. We don't suspend forum access separately - violations affect your entire Cashsule account.",
        description2: "Our goal is to maintain a premium environment. Enforcement decisions are final and at our sole discretion.",
        description3: "Upon suspension or termination, your premium subscription will be immediately cancelled. You will not receive any refund for subscription fees already paid, regardless of remaining subscription time. All premium features will be revoked instantly.",
        warning: "⚠️ Account violations may result in immediate termination without warning and loss of all paid subscription fees."
      },
      legal: {
        title: "Legal Compliance",
        description: "If required by law enforcement or legal authorities, we may be obligated to provide forum content and user information. We will comply with valid legal requests as required by law."
      },
      agreement: {
        title: "Ready to Join Our Premium Community?",
        description: "By accepting, you agree to maintain the high standards that make our premium forum valuable. Welcome to an exclusive community focused on financial growth and mutual support.",
        declineButton: "Not Ready",
        acceptButton: "Accept & Join Premium Forum",
        processing: "Processing..."
      }
    },
    zh: {
      title: "社群論壇使用協議",
      subtitle: "歡迎使用我們的社群論壇！請詳閱以下使用條款並確認同意後方可進入。",
      premiumAccess: {
        title: "高級會員專享服務",
        description: "本社群論壇為高級會員專屬功能，旨在為用戶打造優質的理財討論環境。加入我們，您將與一群志同道合、致力於理性討論與知識分享的優質會員共同交流。",
        highlight: "🌟 這是高級會員專屬功能 - 讓我們一起維護這個優質社群的討論品質"
      },
      communityStandards: {
        title: "社群守則",
        intro: "作為高級會員，請您遵守以下規範：",
        items: [
          "保持友善、理性的討論氛圍",
          "專注於個人理財與投資理財相關話題",
          "尊重並保護所有成員的隱私",
          "使用文明用語，保持專業討論風格",
          "主動檢舉不當內容，共同維護社群品質",
          "積極分享有價值的內容，幫助他人達成理財目標"
        ]
      },
      confidentiality: {
        title: "保密條款與隱私規範",
        intro: "本論壇為會員專屬空間，使用時請務必遵守：",
        items: [
          "嚴禁將論壇內容外流或轉載至其他平台",
          "禁止截圖、複製或以任何形式散佈論壇討論內容",
          "理解並尊重這是私人會員制社群空間",
          "絕不透露任何敏感的個人財務資訊"
        ],
        highlight: "🔒 論壇內的所有討論內容僅限會員內部交流，嚴禁外洩"
      },
      prohibited: {
        title: "禁止行為",
        items: [
          "騷擾、霸凌或任何形式的不當言論",
          "發送廣告、垃圾訊息或偏離主題的內容",
          "將論壇內容分享至其他平台或社群",
          "提供不實資訊或冒用他人身份",
          "從事任何違法行為或發布有害內容"
        ]
      },
      enforcement: {
        title: "違規處理",
        description1: "若違反上述規範，將直接影響您的整個 Cashsule 帳戶使用權限。我們不會單獨處理論壇權限，而是統一管理您的完整帳戶狀態。",
        description2: "為維護優質的社群環境，所有違規處理決定均為最終決定，恕不接受申訴。",
        description3: "一旦遭到停權或終止服務，您的高級會員訂閱將立即取消。無論剩餘訂閱時間多長，您已支付的訂閱費用將不予退還。所有高級功能將立即失效。",
        warning: "⚠️ 嚴重違規者將立即停權，恕不另行通知，且已支付的訂閱費用不予退還"
      },
      legal: {
        title: "法律配合義務",
        description: "如遇執法單位或司法機關依法要求，本平台有義務提供相關論壇內容及用戶資料。我們將依照法律規定配合相關調查作業。"
      },
      agreement: {
        title: "準備好加入我們的高級會員社群了嗎？",
        description: "點選同意即表示您願意遵守上述規範，共同維護這個專注於理財成長與互助學習的優質社群環境。",
        declineButton: "暫不加入",
        acceptButton: "同意條款並加入論壇",
        processing: "處理中..."
      }
    }
  };

  const currentContent = content[language];

  useEffect(() => {
    // Check if user has already accepted the agreement
    const checkAgreementStatus = async () => {
      if (!currentUser) {
        // If no user, redirect to login
        navigate('/login');
        return;
      }
      
      try {
        const agreementDoc = await getDoc(doc(db, 'forumAgreement', currentUser.uid));
        if (agreementDoc.exists() && agreementDoc.data().hasAccepted) {
          // User has already accepted, redirect to forum
          navigate('/community');
        }
      } catch (error) {
        console.error('Error checking agreement status:', error);
        toast.error('Failed to check agreement status. Please try again.');
      }
    };

    checkAgreementStatus();
  }, [currentUser, navigate]);

  const handleAccept = async () => {
    if (!currentUser) {
      toast.error('You must be logged in to accept the agreement.');
      navigate('/login');
      return;
    }
    
    setLoading(true);
    try {
      await setDoc(doc(db, 'forumAgreement', currentUser.uid), {
        userId: currentUser.uid,
        hasAccepted: true,
        acceptedAt: new Date(),
        userEmail: currentUser.email,
        username: currentUser.displayName || currentUser.email
      });
      
      toast.success('Agreement accepted! Welcome to the community forum.');
      navigate('/community');
    } catch (error) {
      console.error('Error saving agreement:', error);
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Please ensure you have the required access level.');
      } else {
        toast.error('Failed to save agreement. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    toast.info('You must accept the agreement to access the community forum.');
    navigate('/dashboard');
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-blue-50 dark:bg-gray-700 select-none">
        <Header />

        {/* Main Content with proper top spacing */}
        <main className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6">
          {/* Language Toggle */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setLanguage(language === 'en' ? 'zh' : 'en')}
              className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FaGlobe className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'en' ? '中文' : 'English'}
              </span>
            </button>
          </div>

          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6 sm:mb-8 md:mb-10"
          >
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl min-h-8 sm:min-h-12 font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
              {currentContent.title}
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400 mt-2 sm:mt-3">
              {currentContent.subtitle}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden"
          >
            <div className="px-4 sm:px-6 py-4">
              <div className="space-y-6 sm:space-y-8">
                {/* Introduction */}
                <motion.section
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                    {currentContent.premiumAccess.title}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-3">
                    {currentContent.premiumAccess.description}
                  </p>
                  <div className="bg-purple-50 dark:bg-purple-900/30 border-l-4 border-purple-400 p-3 sm:p-4 my-3 sm:my-4 rounded-r-lg">
                    <p className="text-purple-700 dark:text-purple-300 font-medium text-sm sm:text-base">
                      {currentContent.premiumAccess.highlight}
                    </p>
                  </div>
                </motion.section>

                {/* Community Standards */}
                <motion.section
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center mb-2 sm:mb-3">
                    <FaUserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {currentContent.communityStandards.title}
                    </h2>
                  </div>
                  <div className="pl-6 sm:pl-7">
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      {currentContent.communityStandards.intro}
                    </p>
                    <ul className="list-disc pl-4 sm:pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3 sm:mb-4 text-sm sm:text-base">
                      {currentContent.communityStandards.items.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </motion.section>

                {/* Confidentiality */}
                <motion.section
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center mb-2 sm:mb-3">
                    <FaShieldAlt className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {currentContent.confidentiality.title}
                    </h2>
                  </div>
                  <div className="pl-6 sm:pl-7">
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      {currentContent.confidentiality.intro}
                    </p>
                    <ul className="list-disc pl-4 sm:pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3 sm:mb-4 text-sm sm:text-base">
                      {currentContent.confidentiality.items.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                    
                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 p-3 sm:p-4 my-3 sm:my-4 rounded-r-lg">
                      <p className="text-blue-700 dark:text-blue-300 font-medium text-sm sm:text-base">
                        {currentContent.confidentiality.highlight}
                      </p>
                    </div>
                  </div>
                </motion.section>

                {/* Prohibited Actions */}
                <motion.section
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center mb-2 sm:mb-3">
                    <FaBan className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {currentContent.prohibited.title}
                    </h2>
                  </div>
                  <div className="pl-6 sm:pl-7">
                    <ul className="list-disc pl-4 sm:pl-5 text-gray-700 dark:text-gray-300 space-y-1 text-sm sm:text-base">
                      {currentContent.prohibited.items.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </motion.section>

                {/* Enforcement */}
                <motion.section
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="flex items-center mb-2 sm:mb-3">
                    <FaExclamationTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {currentContent.enforcement.title}
                    </h2>
                  </div>
                  <div className="pl-6 sm:pl-7">
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      {currentContent.enforcement.description1}
                    </p>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      {currentContent.enforcement.description2}
                    </p>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      {currentContent.enforcement.description3}
                    </p>
                    <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-400 p-3 sm:p-4 mt-3 sm:mt-4 rounded-r-lg">
                      <p className="text-red-700 dark:text-red-300 font-medium text-sm sm:text-base">
                        {currentContent.enforcement.warning}
                      </p>
                    </div>
                  </div>
                </motion.section>

                {/* Legal Compliance */}
                <motion.section
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                    {currentContent.legal.title}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                    {currentContent.legal.description}
                  </p>
                </motion.section>

                {/* Agreement Section */}
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-4 sm:p-6 rounded-lg border border-purple-200 dark:border-purple-800"
                >
                  <div className="flex items-center mb-3 sm:mb-4">
                    <FaFileContract className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 mr-2 sm:mr-3 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {currentContent.agreement.title}
                    </h2>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-4 sm:mb-6">
                    {currentContent.agreement.description}
                  </p>
                  
                  <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center">
                    <button
                      onClick={handleDecline}
                      disabled={loading}
                      className="px-6 sm:px-8 py-2.5 sm:py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      {currentContent.agreement.declineButton}
                    </button>
                    <button
                      onClick={handleAccept}
                      disabled={loading}
                      className="px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent"></div>
                          <span>{currentContent.agreement.processing}</span>
                        </>
                      ) : (
                        currentContent.agreement.acceptButton
                      )}
                    </button>
                  </div>
                </motion.section>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </ThemeProvider>
  );
};

export default ForumAgreementPage;
